/**
 * AdminSection — the `/admin` + `/admin/$section` route bodies (Story UI-U6 AC1/AC2).
 *
 * `AdminIndex` redirects `/admin` to the first section the caller may see (server
 * truth via useEffectivePermissions). `AdminSectionView` dispatches `$section` to
 * its page and renders an honest "Kein Zugriff" state on a direct visit without the
 * key — the api guards remain the hard line; this is guard-AWARENESS from the
 * server's answer, never a client-side re-derivation of role rules.
 *
 * [Source: docs/stories/ui-u6-admin-section.md AC1 + AC2]
 */

import type { ComponentType } from 'react';
import { Navigate } from '@tanstack/react-router';
import {
  type AdminPageKey,
  isAdminPageKey,
  firstVisibleSection,
  requiredKeysFor,
} from './adminNav';
import { useEffectivePermissions } from './useEffectivePermissions';
import { NoAccess, LoadingState } from './components';
import { MitgliederPage } from './pages/MitgliederPage';
import { RollenPage } from './pages/RollenPage';
import { BerechtigungsgruppenPage } from './pages/BerechtigungsgruppenPage';
import { TeamsPage } from './pages/TeamsPage';
import { KundenteamsPage } from './pages/KundenteamsPage';
import { MandantPage } from './pages/MandantPage';
import { RechnungskontrollePage } from './pages/RechnungskontrollePage';
import { RegelwerkPage } from './pages/RegelwerkPage';
import { WorkflowsPage } from './pages/WorkflowsPage';
import { ProzessErinnerungenPage } from './pages/ProzessErinnerungenPage';
import { UnterschriftenPage } from './pages/UnterschriftenPage';
import { MahnwesenPage } from './pages/MahnwesenPage';
import { VersichererFristenPage } from './pages/VersichererFristenPage';
import { BrokermandatMeldungPage } from './pages/BrokermandatMeldungPage';
import { MandatsaufloesungPage } from './pages/MandatsaufloesungPage';
import { SystemStatusPage } from './pages/SystemStatusPage';
import { KontrollePage } from './pages/KontrollePage';
import { AuditLogPage } from './pages/AuditLogPage';
import { PostfaecherPage } from './pages/PostfaecherPage';
import { AusschlusslistePage } from './pages/AusschlusslistePage';
import { EmailBlocklistPage } from './pages/EmailBlocklistPage';
import { SignaturPage } from './pages/SignaturPage';
import { ZeichnungsberechtigtePage } from './pages/ZeichnungsberechtigtePage';

/**
 * Page registry — one component per section key. Sibling story UI-U5 registers its
 * `tarifmatrix` page here (and its adminNav entry) when it lands; this map is the
 * single dispatch point so that stays an additive merge.
 */
const ADMIN_PAGES: Record<AdminPageKey, ComponentType> = {
  mitglieder: MitgliederPage,
  rollen: RollenPage,
  berechtigungsgruppen: BerechtigungsgruppenPage,
  teams: TeamsPage,
  kundenteams: KundenteamsPage,
  mandant: MandantPage,
  rechnungspruefung: RechnungskontrollePage,
  regelwerk: RegelwerkPage,
  workflows: WorkflowsPage,
  'prozess-erinnerungen': ProzessErinnerungenPage,
  unterschriften: UnterschriftenPage,
  mahnwesen: MahnwesenPage,
  'versicherer-fristen': VersichererFristenPage,
  'brokermandat-meldung': BrokermandatMeldungPage,
  mandatsaufloesung: MandatsaufloesungPage,
  'system-status': SystemStatusPage,
  kontrolle: KontrollePage,
  'audit-log': AuditLogPage,
  postfaecher: PostfaecherPage,
  blockliste: EmailBlocklistPage,
  ausschlussliste: AusschlusslistePage,
  signatur: SignaturPage,
  zeichnungsberechtigte: ZeichnungsberechtigtePage,
};

/** `/admin` — redirect to the first section the caller may see. */
export function AdminIndex() {
  const access = useEffectivePermissions();
  if (access.isLoading) return <LoadingState />;
  const first = firstVisibleSection(access);
  if (first == null) {
    return <NoAccess message="Sie haben keinen Zugriff auf den Administrationsbereich." />;
  }
  return <Navigate to="/admin/$section" params={{ section: first }} replace />;
}

/** `/admin/$section` — guard-aware dispatch to the section page. */
export function AdminSectionView({ section }: { section: string }) {
  const access = useEffectivePermissions();

  if (!isAdminPageKey(section)) {
    return <NoAccess message="Diese Seite existiert nicht." />;
  }
  if (access.isLoading) return <LoadingState />;
  if (!access.canAny(requiredKeysFor(section))) {
    return <NoAccess />;
  }

  const Page = ADMIN_PAGES[section];
  return <Page />;
}
